import { Form } from 'antd'
import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import type { MusicGeneratorFormValuesNew } from '@/pages/tad.ai/ai-music-generator/_types/form'
import type { IdeasRenderParams } from '@/pages/tad.ai/ai-music-generator/_components/Ideas'

import style from '@/pages/tad.ai/_components/H2/index.module.scss'
import { RichTextElement } from '@/pages/tad.ai/_components/RichTextElement/RichTextElement'
import CreateMusicIdeas from '@/pages/tad.ai/homepage/_components/CreateMusic/_components/CreateMusicIdeas'
import GeneratorLyricsSmart from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/components/GeneratorLyricsSmart'
import { useInitGeneratorType } from '@/pages/tad.ai/ai-music-generator/_blocks/GeneratorForm/_hooks/initGeneratorType'
import { useMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/useMusicFormStore'
import { PageStoreProvider } from '@/pages/tad.ai/_shared/formStore'
import { createMusicFormStore } from '@/pages/tad.ai/ai-music-generator/_store/formStore'


import { cls } from 'tagged-classnames-free';
const Ideas = dynamic(
  () => import('@/pages/tad.ai/ai-music-generator/_components/Ideas'),
  {
    ssr: false,
  },
)

interface ITopAreaProps {
  h1: string
  h1Desc: string
  btnText?: string
  img: string
}

function TopAreaInner(props: ITopAreaProps) {
  const { h1, h1Desc } = props
  const [form] = Form.useForm<MusicGeneratorFormValuesNew>()
  const formValues = useMusicFormStore((s) => s.form)
  const hasHydrated = useMusicFormStore((s) => s._hasHydrated)
  const setFormValues = useMusicFormStore((s) => s.setFormValues)
  const initializedFormRef = useRef(false)
  const router = useRouter()
  const { onChangeGeneratorType } = useInitGeneratorType()

  useEffect(() => {
    if (!initializedFormRef.current && hasHydrated && formValues) {
      initializedFormRef.current = true
      form.setFieldsValue({
        ...formValues,
      })
    }
  }, [form, formValues, hasHydrated])

  const outwardEvent = (): void => {
    onChangeGeneratorType('auto')
    setFormValues({
      promptTabsMode: 'smart',
    })
    router.push('/ai-music-generator')
  }

  return (
    <div className='relative z-[1] mx-auto max-w-[980px]'>
      <div className='px-4 py-10 text-start lg:text-center'>
        <h1
          className={cls`text-2xl font-bold md:text-7xl ${style.heightLightText}`}
        >
          {h1}
        </h1>
        <RichTextElement
          as='p'
          className='text-f-other-10 [&_a]:text-f-primary px-20 pt-3 text-sm md:pt-4 md:text-base [&_a]:hover:underline'
          html={h1Desc}
        />
      </div>
      <div className='px-4'>
        <Form<MusicGeneratorFormValuesNew>
          layout='vertical'
          preserve
          className={cls`
            scrollbar px-6xl:max-w-none mx-auto w-full max-w-[760px] flex-1 xl:overflow-auto
            [&_[class*="form-item-label"]>label]:text-base
            [&_[class*="form-item-label"]>label]:font-semibold
          `}
          form={form}
          initialValues={{
            promptTabsMode: 'smart',
            promptSelectMode: 'smart',
            customTaskType: 'Song',
            taskType: 'Song',
            thinking: true,
          }}
          onValuesChange={(_, values) => {
            const newValues = {
              ...values,
              imageUrl: values.imageUrl?.startsWith('http')
                ? values.imageUrl
                : undefined,
            }
            setFormValues(newValues)
          }}
          scrollToFirstError
        >
          <GeneratorLyricsSmart
            form={form}
            outwardEvent={outwardEvent}
            textareaStyle={{ resize: 'none' }}
            isEntryPage
          />
          <Ideas
            form={form}
            renderFn={(params: IdeasRenderParams) => {
              return <CreateMusicIdeas params={params} />
            }}
          />
        </Form>
      </div>
    </div>
  )
}

export const TopArea: React.FC<ITopAreaProps> = (props) => {
  return (
    <PageStoreProvider createStore={createMusicFormStore}>
      <TopAreaInner {...props} />
    </PageStoreProvider>
  )
}
